import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Form,
  CardTitle,
  Field,
} from '@cordata/buffet';
import {
  Card,
  CardBody,
  Row,
  Col,
  Button,
  Table,
  FormGroup,
  Label,
  Input,
} from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as Yup from 'yup';
import { format } from 'date-fns';
import { useFeatures } from '@cordata/buffet';
import { adminInstance as axios, toastSuccess, toastError } from '~/utils';
import { useAdmin } from '~/utils/admin';
import { getBadgeClass } from '~/components/SendgridEvent/Form';

interface OutboundEmailAttributes {
  orgUid: string;
  divisionUid: string | null;
  sgMessageId: string;
  fromEmailAddress: string;
  toEmailAddress: string;
  subject: string;
  textBody: string;
  htmlBody: string;
  customArgs: string;
  status: string;
  sentDate: string;
}

interface OutboundEmail {
  id: string;
  type: string;
  attributes: OutboundEmailAttributes;
  links: {
    self: string;
  };
  'info-redacted': boolean;
  relationships: {
    division: {
      related: string;
    };
    org: {
      related: string;
    };
    'sendgrid-events': {
      related: string;
    };
  };
}

interface OutboundEmailFormProps {
  email?: OutboundEmail;
}

const DEFAULT_VALUES = {
  id: '',
  type: '',
  attributes: {
    orgUid: '',
    divisionUid: null,
    sgMessageId: '',
    fromEmailAddress: '',
    toEmailAddress: '',
    subject: '',
    textBody: '',
    htmlBody: '',
    customArgs: '',
    status: '',
    sentDate: '',
  },
  links: {
    self: '',
  },
  'info-redacted': false,
  relationships: {
    division: {
      related: '',
    },
    org: {
      related: '',
    },
    'sendgrid-events': {
      related: '',
    },
  },
};

const formatJson = (jsonString: string | null) => {
  if (!jsonString) return 'N/A';
  try {
    const parsed = JSON.parse(jsonString);
    return JSON.stringify(parsed, null, 2);
  } catch (e) {
    return jsonString;
  }
};

const formatTimestamp = (timestamp: string) => {
  if (!timestamp) return 'N/A';
  return new Date(timestamp).toLocaleString();
};

const OutboundEmailForm = ({
  email,
}: OutboundEmailFormProps) => {
  const history = useHistory();
  const { hasAppKickstarter } = useFeatures();
  const { org } = useAdmin();
  const [isResending, setIsResending] = useState(false);
  const [showResendForm, setShowResendForm] = useState(false);
  const [newEmailAddresses, setNewEmailAddresses] = useState('');
  const [sendgridEvents, setSendgridEvents] = useState<any[]>([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [latestStatus, setLatestStatus] = useState<string | null>(null);

  useEffect(() => {
    if (email?.id) {
      setEventsLoading(true);
      axios
        .get('/sendgrid_event', {
          params: {
            'outbound-email-uid': email.id,
            sort: '-sendgridTimestamp',
          },
        })
        .then(response => {
          const events = response.data.data || [];
          setSendgridEvents(events);
          // Set the latest status from the most recent event
          if (events.length > 0) {
            setLatestStatus(events[0].attributes.event);
          }
        })
        .catch(error => {
          console.error('Error fetching sendgrid events:', error);
          setSendgridEvents([]);
        })
        .finally(() => {
          setEventsLoading(false);
        });
    }
  }, [email?.id, hasAppKickstarter]);

  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const areValidEmails = (emailString: string) => {
    const emails = emailString.split(',').map(email => email.trim()).filter(email => email);
    return emails.length > 0 && emails.every(email => isValidEmail(email));
  };

  const handleResendEmail = async () => {
    if (!email?.id) return;

    if (!newEmailAddresses.trim() || !areValidEmails(newEmailAddresses)) return;

    setIsResending(true);

    try {
      const emails = newEmailAddresses.split(',').map(email => email.trim()).filter(email => email);

      // If multiple emails, send individually to existing endpoint
      if (emails.length === 1) {
        await axios.post('outbound_email/resend', {
          outbound_email_uid: email.id,
          email_address: emails[0],
        });
      } else {
        // Send to each email individually
        await Promise.all(emails.map(emailAddr =>
          axios.post('outbound_email/resend', {
            outbound_email_uid: email.id,
            email_address: emailAddr,
          })
        ));
      }

      toastSuccess('Message has been sent');
      setShowResendForm(false);
      setNewEmailAddresses('');
    } catch (error) {
      toastError('Error occurred while sending message');
    } finally {
      setIsResending(false);
    }
  };

  const validationSchema = Yup.object().shape({
    attributes: Yup.object().shape({
      orgUid: Yup.string().required(),
      divisionUid: Yup.string().nullable(),
      sgMessageId: Yup.string().required(),
      fromEmailAddress: Yup.string().email().required(),
      toEmailAddress: Yup.string().email().required(),
      subject: Yup.string().required(),
      textBody: Yup.string().required(),
      htmlBody: Yup.string().required(),
      customArgs: Yup.string().required(),
      status: Yup.string().required(),
      sentDate: Yup.string().required(),
    }),
    id: Yup.string().required(),
    type: Yup.string().required(),
  });

  return (
    <Form
      displayName="OutboundEmailForm"
      validateOnBlur
      enableReinitialize
      initialValues={{
        ...DEFAULT_VALUES,
        ...(email && email),
      }}
      validationSchema={validationSchema}
      onSubmit={() => {}} // No submit functionality - read-only form
      noValidate
    >
      {({ values }) => (
        <Card>
          <CardBody className="p-0">
            <fieldset>
              <CardTitle>Email Information</CardTitle>

              {/* Resend functionality */}
              <Row form className="mb-3">
                <Col sm={12}>
                  {!showResendForm ? (
                    <Button
                      color="primary"
                      onClick={() => setShowResendForm(true)}
                      className="mb-3"
                    >
                      <FontAwesomeIcon icon="paper-plane" className="mr-2" />
                      Change Target and Resend
                    </Button>
                  ) : (
                    <div className="border p-3 rounded bg-light mb-3">
                      <Row form>
                        <Col sm={8}>
                          <FormGroup>
                            <Label htmlFor="newEmailAddresses">Email address(es)</Label>
                            <Input
                              id="newEmailAddresses"
                              type="textarea"
                              rows={3}
                              value={newEmailAddresses}
                              onChange={(e) => setNewEmailAddresses(e.target.value)}
                              placeholder="Enter one or more email addresses (comma-separated)"
                            />
                            <small className="form-text text-muted">
                              Enter multiple email addresses separated by commas
                            </small>
                          </FormGroup>
                        </Col>
                      </Row>

                      <Row form>
                        <Col sm={12}>
                          <div className="d-flex">
                            <Button
                              color="success"
                              onClick={handleResendEmail}
                              disabled={
                                isResending ||
                                !newEmailAddresses.trim() ||
                                !areValidEmails(newEmailAddresses)
                              }
                              className="mr-2"
                            >
                              {isResending ? (
                                <>
                                  <FontAwesomeIcon icon="spinner" spin className="mr-2" />
                                  Sending...
                                </>
                              ) : (
                                <>
                                  <FontAwesomeIcon icon="paper-plane" className="mr-2" />
                                  Send
                                </>
                              )}
                            </Button>
                            <Button
                              color="secondary"
                              onClick={() => {
                                setShowResendForm(false);
                                setNewEmailAddresses('');
                              }}
                              disabled={isResending}
                            >
                              Cancel
                            </Button>
                          </div>
                        </Col>
                      </Row>
                    </div>
                  )}
                </Col>
              </Row>

              <Row form>
                <Col sm={6}>
                  <Field
                    name="attributes.fromEmailAddress"
                    label="From Email Address"
                    disabled
                  />
                </Col>

                <Col sm={6}>
                  <Field
                    name="attributes.toEmailAddress"
                    label="To Email Address"
                    disabled
                  />
                </Col>
              </Row>

              <Row form>
                <Col sm={6}>
                  <Field
                    name="attributes.status"
                    label="Status"
                    disabled
                    value={
                      eventsLoading ? 'Loading...' : (
                      latestStatus || values.attributes?.status || 'unknown'
                      )
                    }
                    text={
                      eventsLoading ? (
                        <span className="text-muted">Loading...</span>
                      ) : (
                        <span className={`badge badge-${getBadgeClass(latestStatus || values.attributes?.status || '')}`}>
                          {latestStatus || values.attributes?.status || 'unknown'}
                        </span>
                      )
                    }
                  />
                </Col>

                <Col sm={6}>
                  <Field
                    name="attributes.sentDate"
                    label="Sent Date"
                    disabled
                    value={formatTimestamp(values.attributes?.sentDate || '')}
                  />
                </Col>
              </Row>

              <Row form>
                <Col sm={12}>
                  <Field
                    name="attributes.subject"
                    label="Subject"
                    disabled
                  />
                </Col>
              </Row>

              <Row form>
                <Col sm={12}>
                  <Field
                    name={values.attributes?.htmlBody ? 'attributes.htmlBody' : 'attributes.textBody' }
                    label="Body"
                    type="textarea"
                    rows={8}
                    disabled
                    style={{ fontFamily: 'monospace', fontSize: '12px' }}
                  />
                </Col>
              </Row>
            </fieldset>

            <fieldset>
              <CardTitle>Sendgrid Identifiers</CardTitle>

              <Row form>
                <Col sm={12}>
                  <Field
                    name="attributes.sgMessageId"
                    label="Sendgrid Message ID"
                    disabled
                    style={{ fontSize: '12px', wordBreak: 'break-all' }}
                  />
                </Col>
              </Row>
            </fieldset>

            {hasAppKickstarter && (
              <fieldset>
                <CardTitle>Sendgrid Events</CardTitle>

                {eventsLoading ? (
                  <Row form>
                    <Col sm={12}>
                      <div className="text-center p-3">
                        <FontAwesomeIcon icon="spinner" spin className="mr-2" />
                        Loading events...
                      </div>
                    </Col>
                  </Row>
                ) : sendgridEvents.length === 0 ? (
                  <Row form>
                    <Col sm={12}>
                      <div className="text-muted p-3">
                        No events recorded.
                      </div>
                    </Col>
                  </Row>
                ) : (
                  <Row form>
                    <Col sm={12}>
                      <Table hover size="sm" className="mb-0">
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Event Type</th>
                            <th>Reason</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {sendgridEvents.map((event) => (
                            <tr
                              key={event.id}
                              style={{ cursor: 'pointer' }}
                              onClick={() => history.push(`/admin/sendgrid-events/${event.id}`)}
                            >
                              <td>
                                {event.attributes.sendgridTimestamp
                                  ? format(event.attributes.sendgridTimestamp, 'ddd MMM Do YYYY [@] h:mm A')
                                  : 'N/A'}
                              </td>
                              <td>
                                <span className={`badge badge-${getBadgeClass(event.attributes.event)}`}>
                                  {event.attributes.event}
                                </span>
                              </td>
                              <td>{event.attributes.reason || '-'}</td>
                              <td className="text-right">
                                <FontAwesomeIcon icon="chevron-right" size="sm" />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </Col>
                  </Row>
                )}
              </fieldset>
            )}
          </CardBody>
        </Card>
      )}
    </Form>
  );
};

export default OutboundEmailForm;
